import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  Clock,
  Shield,
  User,
  Calendar,
  FileText,
  Play,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface VideoKycDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onSuccess?: () => void;
}

type KycStep = "idle" | "starting" | "in-progress" | "completed" | "failed" | "abandoned";

interface JourneyState {
  step: KycStep;
  journeyId: string | null;
  journeyUrl: string | null;
  signzyStatus: string | null;
  currentStep: string | null;
  results: any | null;
  error: string | null;
  showFallback: boolean;
}

export function VideoKycDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  customerEmail,
  customerPhone,
  onSuccess,
}: VideoKycDialogProps) {
  const { toast } = useToast();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [state, setState] = useState<JourneyState>({
    step: "idle",
    journeyId: null,
    journeyUrl: null,
    signzyStatus: null,
    currentStep: null,
    results: null,
    error: null,
    showFallback: false,
  });

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const handleReset = useCallback(() => {
    stopPolling();
    setState({
      step: "idle",
      journeyId: null,
      journeyUrl: null,
      signzyStatus: null,
      currentStep: null,
      results: null,
      error: null,
      showFallback: false,
    });
  }, [stopPolling]);

  // Clean up polling on unmount or close
  useEffect(() => {
    if (!open) {
      stopPolling();
    }
    return () => stopPolling();
  }, [open, stopPolling]);

  // Poll journey status
  const pollStatus = useCallback(async (journeyId: string) => {
    try {
      const response = await apiRequest("GET", `/api/kyc/status/${journeyId}`);
      const data = await response.json();

      setState((s) => ({
        ...s,
        signzyStatus: data.signzyStatus,
        currentStep: data.step,
      }));

      if (data.signzyStatus === "COMPLETED") {
        stopPolling();
        // Fetch full results
        try {
          const resultsResponse = await apiRequest("GET", `/api/kyc/results/${journeyId}`);
          const resultsData = await resultsResponse.json();
          setState((s) => ({
            ...s,
            step: "completed",
            results: resultsData,
          }));
          queryClient.invalidateQueries({ queryKey: ["customers"] });
          queryClient.invalidateQueries({ queryKey: ["customer-detail"] });
          toast({
            title: "KYC Complete",
            description: "KYC journey completed successfully.",
          });
          onSuccess?.();
        } catch {
          setState((s) => ({
            ...s,
            step: "completed",
            results: null,
          }));
        }
      } else if (data.signzyStatus === "TERMINATED") {
        stopPolling();
        setState((s) => ({ ...s, step: "abandoned", error: "User exited the KYC journey." }));
      } else if (data.signzyStatus === "ABANDONED") {
        stopPolling();
        setState((s) => ({ ...s, step: "abandoned", error: "KYC session timed out." }));
      }
    } catch {
      // Silently continue polling on transient errors
    }
  }, [stopPolling, toast, onSuccess]);

  // Start journey mutation
  const startMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/kyc/start", {
        customerId,
        name: customerName,
        email: customerEmail,
        phone: customerPhone,
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to start KYC journey");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success && data.journeyUrl) {
        setState((s) => ({
          ...s,
          step: "in-progress",
          journeyId: data.journeyId,
          journeyUrl: data.journeyUrl,
          error: null,
          showFallback: false,
        }));

        // Start polling
        pollingRef.current = setInterval(() => {
          pollStatus(data.journeyId);
        }, 5000);

        // Show fallback "Open in new tab" after 5 seconds
        fallbackTimerRef.current = setTimeout(() => {
          setState((s) => ({ ...s, showFallback: true }));
        }, 5000);

        toast({
          title: "KYC Journey Started",
          description: "Complete the verification in the window below.",
        });
      } else {
        setState((s) => ({
          ...s,
          step: "failed",
          error: "No journey URL returned from server.",
        }));
      }
    },
    onError: (error: Error) => {
      setState((s) => ({
        ...s,
        step: "failed",
        error: error.message,
      }));
    },
  });

  const handleStart = () => {
    setState((s) => ({ ...s, step: "starting" }));
    startMutation.mutate();
  };

  // Step indicator
  const steps = ["Start", "In Progress", "Complete"];
  const stepIndex = {
    idle: -1,
    starting: 0,
    "in-progress": 1,
    completed: 2,
    failed: -1,
    abandoned: -1,
  }[state.step];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "p-0 gap-0 overflow-hidden max-h-[95vh]",
        state.step === "in-progress" ? "sm:max-w-[90vw] lg:max-w-6xl" : "sm:max-w-lg"
      )}>
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            KYC Verification
          </DialogTitle>
          <DialogDescription>
            {customerName
              ? `KYC verification for ${customerName}`
              : "Complete identity verification via KYC"}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        {state.step !== "failed" && state.step !== "abandoned" && (
          <div className="flex items-center justify-between px-6 pb-4">
            {steps.map((step, i) => (
              <div key={step} className="flex items-center">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                    i < stepIndex
                      ? "bg-primary text-primary-foreground"
                      : i === stepIndex
                      ? "bg-primary/20 text-primary border-2 border-primary"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {i < stepIndex ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={cn(
                      "w-16 h-0.5 mx-1",
                      i < stepIndex ? "bg-primary" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="px-6 pb-6">
          {/* Idle - Start button */}
          {state.step === "idle" && (
            <div className="space-y-4">
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertTitle>Signzy KYC</AlertTitle>
                <AlertDescription>
                  Complete identity verification through document capture and
                  liveness check.
                </AlertDescription>
              </Alert>

              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <h4 className="font-medium text-sm">Journey Steps:</h4>
                <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
                  <li>Consent & country selection</li>
                  <li>Document capture (front & back)</li>
                  <li>Document verification check</li>
                  <li>Face liveness capture</li>
                  <li>Verification complete</li>
                </ol>
              </div>

              <Button className="w-full" onClick={handleStart}>
                <Shield className="mr-2 h-4 w-4" />
                Start KYC
              </Button>
            </div>
          )}

          {/* Starting */}
          {state.step === "starting" && (
            <div className="flex flex-col items-center py-8 gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-muted-foreground">Starting KYC journey...</p>
            </div>
          )}

          {/* In Progress - Toggle between KYC iframe and demo video */}
          {state.step === "in-progress" && state.journeyUrl && (
            <div className="space-y-3">
              {/* Toggle tabs */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setShowVideo(false)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                    !showVideo
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Shield className="h-4 w-4" />
                  Live KYC
                </button>
                <button
                  onClick={() => setShowVideo(true)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                    showVideo
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Play className="h-4 w-4" />
                  Demo Video
                </button>
              </div>

              {/* Content area */}
              <div className="rounded-lg overflow-hidden border" style={{ position: "relative" }}>
                {/* KYC iframe - always mounted, hidden when video shown */}
                <iframe
                  src={state.journeyUrl}
                  allow="camera; microphone; geolocation; fullscreen"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="Signzy KYC Verification"
                  className="w-full border-none"
                  style={{
                    height: "70vh",
                    maxHeight: "650px",
                    display: showVideo ? "none" : "block",
                  }}
                />

                {/* Demo video */}
                {showVideo && (
                  <div
                    className="bg-black"
                    style={{ height: "70vh", maxHeight: "650px", position: "relative" }}
                  >
                    <iframe
                      src="https://player.vimeo.com/video/1014321850?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      title="One Touch KYC Solution by Signzy"
                      className="absolute inset-0 w-full h-full border-none"
                    />
                  </div>
                )}
              </div>

              {/* Status + controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-600">
                    <Clock className="h-3 w-3 mr-1" />
                    {state.currentStep || "Waiting"}
                  </Badge>
                  {state.signzyStatus && (
                    <Badge variant="secondary">{state.signzyStatus}</Badge>
                  )}
                </div>

                {state.showFallback && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      window.open(state.journeyUrl!, "_blank", "noopener,noreferrer")
                    }
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open in New Tab
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Completed */}
          {state.step === "completed" && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold">KYC Journey Complete</h3>
                <p className="text-muted-foreground">KYC has been submitted for review.</p>
              </div>

              {/* Results */}
              {state.results?.documentIntelligence?.extractedFields && (
                <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                  <h4 className="font-medium flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-primary" />
                    Extracted Document Data
                  </h4>
                  <div className="grid gap-2 text-sm">
                    {state.results.documentIntelligence.extractedFields.name && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" /> Name
                        </span>
                        <span className="font-medium">{state.results.documentIntelligence.extractedFields.name}</span>
                      </div>
                    )}
                    {state.results.documentIntelligence.extractedFields.dateOfBirth && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" /> Date of Birth
                        </span>
                        <span className="font-medium">{state.results.documentIntelligence.extractedFields.dateOfBirth}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {state.results?.selfieAnalysis?.livenessScore && (
                <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                  <h4 className="font-medium flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-primary" />
                    Liveness & Face Match
                  </h4>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Liveness Score</span>
                      <span className="font-medium">{state.results.selfieAnalysis.livenessScore}%</span>
                    </div>
                    {state.results.selfieAnalysis.faceMatchPercentage && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Face Match</span>
                        <span className="font-medium">{state.results.selfieAnalysis.faceMatchPercentage}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button className="w-full" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          )}

          {/* Failed */}
          {state.step === "failed" && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="mx-auto h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold">Journey Failed</h3>
                <p className="text-muted-foreground">{state.error}</p>
              </div>
              <Button className="w-full" onClick={handleReset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          )}

          {/* Abandoned / Terminated */}
          {state.step === "abandoned" && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="mx-auto h-16 w-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
                <h3 className="text-lg font-semibold">Journey Interrupted</h3>
                <p className="text-muted-foreground">{state.error}</p>
              </div>
              <Button className="w-full" onClick={handleReset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Start New Journey
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
